import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { eventService } from "../../../services/api/eventService";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { Event } from "../../../types/event";
import MediaSelector from "../MediaSelector";

interface EventEditModalProps {
  open: boolean;
  event: Event;
  onClose: () => void;
  onUpdate: () => void; // Callback to trigger a re-fetch in the parent
}

const STATUS_OPTIONS: Event["status"][] = ["upcoming", "completed", "cancelled"];

const EventEditModal: React.FC<EventEditModalProps> = ({
  open,
  event,
  onClose,
  onUpdate,
}) => {
  const { tokens } = useAuth();
  const [formData, setFormData] = useState<Event>(event);

  // Synchronize local state with any changes in the "event" prop.
  useEffect(() => {
    setFormData(event);
  }, [event]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStatusChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setFormData({ ...formData, status: e.target.value as Event["status"] });
  };

  const handleImageSelect = (url: string) => {
    setFormData({ ...formData, imageUrl: url });
  };

  const handleSubmit = async () => {
    if (!tokens?.idToken) {
      toast.error("User is not authenticated.");
      return;
    }

    try {
      await eventService.updateEvent(tokens.idToken, event.id, formData);
      toast.success("Event updated successfully.");
      onUpdate(); // Trigger a re-fetch of events in the parent
      onClose();
    } catch (error) {
      toast.error("Failed to update event.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Event</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField label="Title" name="title" value={formData.title} onChange={handleChange} fullWidth />
          <TextField
            label="Date"
            name="date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Time"
            name="time"
            type="time"
            InputLabelProps={{ shrink: true }}
            value={formData.time}
            onChange={handleChange}
            fullWidth
          />
          <TextField select label="Status" name="status" value={formData.status} onChange={handleStatusChange} fullWidth>
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Location" name="location" value={formData.location} onChange={handleChange} fullWidth />
          <TextField label="Organizer" name="organizer" value={formData.organizer} onChange={handleChange} fullWidth />
          <MediaSelector 
            mediaType="image"
            label="Event Image"
            initialMediaUrl={formData.imageUrl || ''}
            onSelect={handleImageSelect}
          />
          <TextField label="Description" name="description" value={formData.description} onChange={handleChange} multiline rows={4} fullWidth />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Save
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventEditModal;
